import { ChevronLeft, ChevronRight, Download, File, Loader2, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import blobService from "../../../../services/blobService";
import { BucketContext, ParsedFileInfo } from "../../../../types/blob";
import ImagePreview from "./ImagePreview";
import PdfPreview from "./PdfPreview";

interface InlineFilePreviewProps {
  files: ParsedFileInfo[];
  currentIndex: number | null;
  context: BucketContext;
  onNavigate: (index: number) => void;
  onClose: () => void;
  onDownload: (file: ParsedFileInfo) => void;
}

const InlineFilePreview: React.FC<InlineFilePreviewProps> = ({
  files,
  currentIndex,
  context,
  onNavigate,
  onClose,
  onDownload,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const previewableFiles = useMemo(
    () => files.filter((f) => f.isPreviewable),
    [files]
  );

  const currentPreviewFile = useMemo(() => {
    if (currentIndex === null) return null;
    return previewableFiles[currentIndex] || null;
  }, [currentIndex, previewableFiles]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!currentPreviewFile) {
      setBlobUrl(null);
      return;
    }

    let cancelled = false;

    const loadFile = async () => {
      setLoading(true);
      setError(null);

      try {
        let blob: Blob;
        if (context.bucketType === "provider") {
          blob = await blobService.downloadProviderFile(
            context.providerId,
            currentPreviewFile.displayName,
            currentPreviewFile.fileSubfolder || undefined
          );
        } else {
          const folder = currentPreviewFile.fileSubfolder || context.folder;
          blob = await blobService.downloadRunPlutusFile(
            folder,
            currentPreviewFile.displayName
          );
        }

        if (!cancelled) {
          const url = URL.createObjectURL(blob);
          setBlobUrl(url);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load file"
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadFile();

    return () => {
      cancelled = true;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [context, currentPreviewFile]);

  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  const hasPrev = currentIndex !== null && currentIndex > 0;
  const hasNext =
    currentIndex !== null && currentIndex < previewableFiles.length - 1;

  const handlePrev = () => {
    if (hasPrev && currentIndex !== null) {
      onNavigate(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (hasNext && currentIndex !== null) {
      onNavigate(currentIndex + 1);
    }
  };

  if (!currentPreviewFile) {
    return (
      <div
        className={`h-full flex items-center justify-center px-4 text-sm ${
          isDarkMode ? "text-rp-grey/50" : "text-gray-500"
        }`}
      >
        Select a previewable file to view here.
      </div>
    );
  }

  return (
    <div
      className={`h-full flex flex-col ${
        isDarkMode ? "bg-rp-black text-rp-grey" : "bg-white text-gray-900"
      }`}
    >
      <div
        className={`flex items-center justify-between px-4 py-3 border-b ${
          isDarkMode ? "border-rp-separator" : "border-gray-200"
        }`}
      >
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1">
            <button
              onClick={handlePrev}
              disabled={!hasPrev}
              className={`p-2 rounded-lg transition-colors ${
                hasPrev
                  ? isDarkMode
                    ? "hover:bg-rp-surface-raised text-rp-grey"
                    : "hover:bg-gray-100 text-gray-700"
                  : "opacity-30 cursor-not-allowed"
              }`}
              aria-label="Previous file"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleNext}
              disabled={!hasNext}
              className={`p-2 rounded-lg transition-colors ${
                hasNext
                  ? isDarkMode
                    ? "hover:bg-rp-surface-raised text-rp-grey"
                    : "hover:bg-gray-100 text-gray-700"
                  : "opacity-30 cursor-not-allowed"
              }`}
              aria-label="Next file"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div>
            <div className="text-sm font-medium truncate">
              {currentPreviewFile.displayName}
            </div>
            <div className="text-xs truncate">
              {currentPreviewFile.formattedSize} -{" "}
              {currentPreviewFile.contentType}
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => onDownload(currentPreviewFile)}
            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
              isDarkMode
                ? "bg-rp-surface hover:bg-rp-surface-raised"
                : "bg-gray-100 hover:bg-gray-200"
            }`}
          >
            <Download className="w-4 h-4" />
            <span>Download</span>
          </button>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode
                ? "hover:bg-rp-surface-raised text-rp-grey/70"
                : "hover:bg-gray-100 text-gray-700"
            }`}
            aria-label="Close preview"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 relative overflow-hidden">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div
              className={`w-full rounded-lg p-3 text-sm ${
                isDarkMode ? "bg-red-900/30 text-red-300" : "bg-red-50 text-red-700"
              }`}
            >
              Failed to load file: {error}
            </div>
          </div>
        )}

        {!loading && !error && blobUrl && (
          <div className="absolute inset-0 overflow-auto p-2">
            {currentPreviewFile.isImage && (
              <ImagePreview src={blobUrl} alt={currentPreviewFile.displayName} />
            )}
            {currentPreviewFile.isPdf && (
              <PdfPreview src={blobUrl} fileName={currentPreviewFile.displayName} />
            )}
            {!currentPreviewFile.isImage && !currentPreviewFile.isPdf && (
              <div className="h-full flex items-center justify-center">
                <div className="text-center space-y-3">
                  <File className="w-12 h-12 text-gray-400 mx-auto" />
                  <p className={isDarkMode ? "text-rp-grey/70" : "text-gray-700"}>
                    Preview not available for this file type.
                  </p>
                  <button
                    onClick={() => onDownload(currentPreviewFile)}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  >
                    Download File
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InlineFilePreview;

